import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TopNavigation from "components/ui/TopNavigation";
import Icon from "components/AppIcon";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const statusColors = {
  active: "var(--color-success)",
  inactive: "#6b7280",
  suspended: "var(--color-error)",
  trial: "var(--color-warning)",
};

function Section({ title, icon, children }) {
  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[var(--color-border)] bg-[var(--color-muted)]/30">
        <Icon name={icon} size={15} color="var(--color-primary)" />
        <h2 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function MemberProfile() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [member, setMember] = useState(null);
  const [trainer, setTrainer] = useState(null);
  const [packages, setPackages] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Notes state
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  useEffect(() => {
    if (memberId) fetchAll();
  }, [memberId]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: memberData } = await api.get('/members/' + memberId);
      if (!memberData) throw new Error('Member not found');
      setMember(memberData);
      setNotes(memberData?.notes || '');

      if (memberData?.trainer_name) {
        setTrainer({ full_name: memberData?.trainer_name });
      }

      const { data: pkgData } = await api.get('/members/' + memberId + '/packages');
      setPackages(pkgData || []);

      const [attRes, payRes] = await Promise.all([
        api.get('/attendances', { params: { member_id: memberId, limit: 50 } }),
        api.get('/payments', { params: { member_id: memberId } }),
      ]);
      setAttendances(attRes?.data || []);
      setPayments(payRes?.data || []);
    } catch (err) {
      console.error("Error fetching member profile:", err);
      setError("Failed to load member profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      await api.patch('/members/' + memberId, { notes });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
    } catch (err) {
      console.error("Error saving notes:", err);
    } finally {
      setSavingNotes(false);
    }
  };

  const activePackage = packages?.find(p => p?.is_active);

  const getRenewalStatus = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const today = new Date();
    const diffDays = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { label: `Expired ${Math.abs(diffDays)}d ago`, color: "var(--color-error)", bg: "bg-red-50" };
    if (diffDays <= 7) return { label: `Expires in ${diffDays}d`, color: "var(--color-warning)", bg: "bg-amber-50" };
    return { label: `${diffDays}d remaining`, color: "var(--color-success)", bg: "bg-green-50" };
  };

  const paymentStatusColor = {
    paid: "var(--color-success)",
    pending: "var(--color-warning)",
    partial: "#8b5cf6",
    overdue: "var(--color-error)",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)]">
        <TopNavigation />
        <main className="pt-[60px] flex items-center justify-center min-h-[calc(100vh-60px)]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Loading member profile…</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="min-h-screen bg-[var(--color-background)]">
        <TopNavigation />
        <main className="pt-[60px] flex items-center justify-center min-h-[calc(100vh-60px)]">
          <div className="text-center">
            <p className="text-sm text-[var(--color-error)] font-[var(--font-caption)] mb-3">{error || "Member not found."}</p>
            <button onClick={() => navigate(-1)} className="text-xs text-[var(--color-primary)] underline">Go back</button>
          </div>
        </main>
      </div>
    );
  }

  const renewalStatus = getRenewalStatus(activePackage?.end_date);

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="pt-[5px]">
        {/* Header */}
        <div className="bg-[var(--color-card)] border-b border-[var(--color-border)] px-4 md:px-6 py-4">
          <div className="max-w-screen-xl mx-auto">
            <div className="flex items-center gap-3 mb-1">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1.5 text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors"
              >
                <Icon name="ArrowLeft" size={13} color="currentColor" />
                Back
              </button>
              <span className="text-[var(--color-border)]">/</span>
              <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Member Profile</span>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-3">
              {/* Avatar */}
              <div className="w-14 h-14 rounded-full bg-[var(--color-primary)]/15 flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-bold text-[var(--color-primary)] font-[var(--font-heading)]">
                  {member?.first_name?.[0]?.toUpperCase()}{member?.last_name?.[0]?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">
                    {member?.first_name} {member?.last_name}
                  </h1>
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize font-[var(--font-caption)]"
                    style={{ color: statusColors?.[member?.member_status] || "#6b7280", backgroundColor: `${statusColors?.[member?.member_status] || "#6b7280"}18` }}
                  >
                    {member?.member_status}
                  </span>
                  {renewalStatus && (
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-semibold font-[var(--font-caption)]"
                      style={{ color: renewalStatus?.color, backgroundColor: `${renewalStatus?.color}18` }}
                    >
                      {renewalStatus?.label}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  {member?.email && (
                    <span className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                      <Icon name="Mail" size={11} color="currentColor" />
                      {member?.email}
                    </span>
                  )}
                  {member?.phone && (
                    <span className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                      <Icon name="Phone" size={11} color="currentColor" />
                      {member?.phone}
                    </span>
                  )}
                  {member?.join_date && (
                    <span className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                      <Icon name="CalendarDays" size={11} color="currentColor" />
                      Joined {new Date(member?.join_date)?.toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 py-6 space-y-6">
          {/* Top row: Package + Trainer */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Package Details */}
            <Section title="Package & Membership" icon="Package">
              {activePackage ? (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">
                        {activePackage?.package_name || "—"}
                      </p>
                      {activePackage?.package_type && (
                        <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5 capitalize">
                          {activePackage?.package_type}
                        </p>
                      )}
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 font-[var(--font-caption)] whitespace-nowrap">Active</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-[var(--radius-sm)] bg-[var(--color-muted)]/40 border border-[var(--color-border)]">
                      <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] uppercase tracking-wide mb-0.5">Type</p>
                      <p className="text-sm font-bold font-[var(--font-data)] text-[var(--color-foreground)] capitalize">
                        {activePackage?.package_type || "—"}
                      </p>
                    </div>
                    <div className="p-3 rounded-[var(--radius-sm)] bg-[var(--color-muted)]/40 border border-[var(--color-border)]">
                      <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] uppercase tracking-wide mb-0.5">Price</p>
                      <p className="text-sm font-bold font-[var(--font-data)] text-[var(--color-foreground)]">
                        {activePackage?.price != null ? `$${parseFloat(activePackage?.price)?.toFixed(2)}` : "—"}
                      </p>
                    </div>
                    <div className="p-3 rounded-[var(--radius-sm)] bg-[var(--color-muted)]/40 border border-[var(--color-border)]">
                      <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] uppercase tracking-wide mb-0.5">Start Date</p>
                      <p className="text-sm font-bold font-[var(--font-data)] text-[var(--color-foreground)]">
                        {activePackage?.start_date ? new Date(activePackage?.start_date)?.toLocaleDateString() : "—"}
                      </p>
                    </div>
                    <div className="p-3 rounded-[var(--radius-sm)] bg-[var(--color-muted)]/40 border border-[var(--color-border)]">
                      <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] uppercase tracking-wide mb-0.5">Renewal Date</p>
                      <p className="text-sm font-bold font-[var(--font-data)]" style={{ color: renewalStatus?.color || "var(--color-foreground)" }}>
                        {activePackage?.end_date ? new Date(activePackage?.end_date)?.toLocaleDateString() : "—"}
                      </p>
                    </div>
                  </div>
                  {renewalStatus && (
                    <div
                      className="flex items-center gap-2 px-3 py-2 rounded-[var(--radius-sm)] border"
                      style={{ borderColor: `${renewalStatus?.color}40`, backgroundColor: `${renewalStatus?.color}10` }}
                    >
                      <Icon name="Clock" size={13} color={renewalStatus?.color} />
                      <span className="text-xs font-semibold font-[var(--font-caption)]" style={{ color: renewalStatus?.color }}>
                        {renewalStatus?.label}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No active package assigned.</p>
              )}

              {/* Past packages */}
              {packages?.filter(p => !p?.is_active)?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
                  <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide mb-2">Past Packages</p>
                  <div className="space-y-2">
                    {packages?.filter(p => !p?.is_active)?.map(p => (
                      <div key={p?.id} className="flex items-center justify-between text-xs font-[var(--font-caption)]">
                        <span className="text-[var(--color-foreground)]">{p?.package_name || "—"}</span>
                        <span className="text-[var(--color-muted-foreground)]">
                          {p?.start_date ? new Date(p?.start_date)?.toLocaleDateString() : "—"} → {p?.end_date ? new Date(p?.end_date)?.toLocaleDateString() : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* Assigned Trainer */}
            <Section title="Assigned Trainer / Staff" icon="UserCheck">
              {trainer ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[var(--color-secondary)]/15 flex items-center justify-center flex-shrink-0">
                      <span className="text-base font-bold text-[var(--color-secondary)] font-[var(--font-heading)]">
                        {trainer?.full_name?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">{trainer?.full_name}</p>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize font-[var(--font-caption)] bg-[var(--color-secondary)]/15 text-[var(--color-secondary)]">
                        {trainer?.role}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {trainer?.email && (
                      <div className="flex items-center gap-2 text-xs font-[var(--font-caption)] text-[var(--color-muted-foreground)]">
                        <Icon name="Mail" size={12} color="currentColor" />
                        {trainer?.email}
                      </div>
                    )}
                    {trainer?.phone && (
                      <div className="flex items-center gap-2 text-xs font-[var(--font-caption)] text-[var(--color-muted-foreground)]">
                        <Icon name="Phone" size={12} color="currentColor" />
                        {trainer?.phone}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 py-4 text-center">
                  <div className="w-10 h-10 rounded-full bg-[var(--color-muted)] flex items-center justify-center">
                    <Icon name="UserX" size={18} color="var(--color-muted-foreground)" />
                  </div>
                  <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No trainer assigned</p>
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Assign a trainer from the Members roster.</p>
                </div>
              )}
            </Section>
          </div>

          {/* Attendance Timeline */}
          <Section title="Attendance Timeline" icon="CalendarCheck">
            {attendances?.length === 0 ? (
              <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] text-center py-4">No attendance records found.</p>
            ) : (
              <div className="space-y-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--color-border)]">
                        {["Date", "Check In", "Check Out", "Duration", "Notes"]?.map(h => (
                          <th key={h} className="pb-2 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide pr-4">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-border)]">
                      {attendances?.map(a => {
                        const checkIn = a?.check_in_time ? new Date(a?.check_in_time) : null;
                        const checkOut = a?.check_out_time ? new Date(a?.check_out_time) : null;
                        let duration = "—";
                        if (checkIn && checkOut) {
                          const mins = Math.round((checkOut - checkIn) / 60000);
                          duration = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
                        }
                        return (
                          <tr key={a?.id} className="hover:bg-[var(--color-muted)]/20 transition-colors">
                            <td className="py-2.5 pr-4 text-xs font-medium font-[var(--font-data)] text-[var(--color-foreground)] whitespace-nowrap">
                              {a?.date ? new Date(a?.date)?.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "—"}
                            </td>
                            <td className="py-2.5 pr-4 text-xs font-[var(--font-data)] text-[var(--color-muted-foreground)] whitespace-nowrap">
                              {checkIn ? checkIn?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                            </td>
                            <td className="py-2.5 pr-4 text-xs font-[var(--font-data)] text-[var(--color-muted-foreground)] whitespace-nowrap">
                              {checkOut ? checkOut?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                            </td>
                            <td className="py-2.5 pr-4 text-xs font-[var(--font-data)] text-[var(--color-foreground)]">{duration}</td>
                            <td className="py-2.5 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{a?.notes || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-3">
                  Showing last {attendances?.length} attendance records.
                </p>
              </div>
            )}
          </Section>

          {/* Payment Records */}
          <Section title="Payment Records" icon="CreditCard">
            {payments?.length === 0 ? (
              <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] text-center py-4">No payment records found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      {["Date", "Amount", "Paid", "Pending", "Method", "Status", "Notes"]?.map(h => (
                        <th key={h} className="pb-2 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide pr-4">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-border)]">
                    {payments?.map(p => (
                      <tr key={p?.id} className="hover:bg-[var(--color-muted)]/20 transition-colors">
                        <td className="py-2.5 pr-4 text-xs font-[var(--font-data)] text-[var(--color-foreground)] whitespace-nowrap">
                          {p?.paid_date ? new Date(p?.paid_date)?.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                        </td>
                        <td className="py-2.5 pr-4 text-xs font-bold font-[var(--font-data)] text-[var(--color-foreground)]">
                          {p?.amount != null ? `$${parseFloat(p?.amount)?.toFixed(2)}` : "—"}
                        </td>
                        <td className="py-2.5 pr-4 text-xs font-[var(--font-data)] text-[var(--color-success)]">
                          {p?.paid_amount != null ? `$${parseFloat(p?.paid_amount)?.toFixed(2)}` : "—"}
                        </td>
                        <td className="py-2.5 pr-4 text-xs font-[var(--font-data)]" style={{ color: parseFloat(p?.pending_amount) > 0 ? "var(--color-error)" : "var(--color-muted-foreground)" }}>
                          {p?.pending_amount != null ? `$${parseFloat(p?.pending_amount)?.toFixed(2)}` : "—"}
                        </td>
                        <td className="py-2.5 pr-4 text-xs capitalize font-[var(--font-caption)] text-[var(--color-muted-foreground)]">
                          {p?.payment_method?.replace("_", " ") || "—"}
                        </td>
                        <td className="py-2.5 pr-4">
                          <span
                            className="px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize font-[var(--font-caption)]"
                            style={{
                              color: paymentStatusColor?.[p?.payment_status] || "#6b7280",
                              backgroundColor: `${paymentStatusColor?.[p?.payment_status] || "#6b7280"}18`,
                            }}
                          >
                            {p?.payment_status}
                          </span>
                        </td>
                        <td className="py-2.5 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{p?.notes || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          {/* Member Notes */}
          <Section title="Member Notes" icon="StickyNote">
            <div className="space-y-3">
              <textarea
                value={notes}
                onChange={e => setNotes(e?.target?.value)}
                rows={5}
                placeholder="Add notes about this member (health conditions, preferences, goals, etc.)…"
                className="w-full px-3 py-2.5 text-sm border border-[var(--color-border)] rounded-[var(--radius-sm)] bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] resize-none font-[var(--font-body)]"
              />
              <div className="flex items-center justify-between">
                {notesSaved && (
                  <span className="flex items-center gap-1.5 text-xs text-[var(--color-success)] font-[var(--font-caption)]">
                    <Icon name="CheckCircle" size={12} color="var(--color-success)" />
                    Notes saved
                  </span>
                )}
                {!notesSaved && <span />}
                <button
                  onClick={handleSaveNotes}
                  disabled={savingNotes}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60"
                >
                  {savingNotes ? (
                    <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
                  ) : (
                    <><Icon name="Save" size={12} color="white" />Save Notes</>
                  )}
                </button>
              </div>
            </div>
          </Section>
        </div>
      </main>
    </div>
  );
}
